import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { MantineProvider, createTheme } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/charts/styles.css';
import './styles.css';

import App from './App';
import { useSettings, applyFontScale } from './store/settings';

// Dropdown/calendar/menu popovers must float ABOVE the interactive-lessons
// overlay (its card sits at z-index 9002; see lessons/LessonOverlay.tsx) so a
// guided step can ask the user to pick from a Select or DatePicker. 9500 keeps
// them under the full-screen connection-lost overlay (z-index 10000). Mantine's
// default is 300, which the lesson card would otherwise cover.
const POPOVER_Z = 9500;

const theme = createTheme({
  primaryColor: 'orange',
  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  defaultRadius: 'sm',
  components: {
    Popover: { defaultProps: { zIndex: POPOVER_Z } },
    Combobox: { defaultProps: { zIndex: POPOVER_Z } },
    Select: { defaultProps: { comboboxProps: { zIndex: POPOVER_Z } } },
    MultiSelect: { defaultProps: { comboboxProps: { zIndex: POPOVER_Z } } },
    Autocomplete: { defaultProps: { comboboxProps: { zIndex: POPOVER_Z } } },
    Menu: { defaultProps: { zIndex: POPOVER_Z } },
    HoverCard: { defaultProps: { zIndex: POPOVER_Z } },
    DateInput: { defaultProps: { popoverProps: { zIndex: POPOVER_Z } } },
    DatePickerInput: { defaultProps: { popoverProps: { zIndex: POPOVER_Z } } },
    DateTimePicker: { defaultProps: { popoverProps: { zIndex: POPOVER_Z } } },
  },
});

function AppShellWithSettings() {
  const themeChoice = useSettings((s) => s.theme);
  const fontScale = useSettings((s) => s.fontScale);

  useEffect(() => { applyFontScale(fontScale); }, [fontScale]);

  return (
    <MantineProvider theme={theme} forceColorScheme={themeChoice === 'auto' ? undefined : themeChoice} defaultColorScheme="auto">
      <ModalsProvider>
        <Notifications position="top-right" />
        <App />
      </ModalsProvider>
    </MantineProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <AppShellWithSettings />
    </BrowserRouter>
  </React.StrictMode>
);
